import { FeatureFlag } from "@coeff/api";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { AppState } from "src/store";
import styled from "styled-components";

import { Option } from "../../components";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { Select } from "../../components/Select";
import { useTrack } from "../../utils";

import { WorkspaceAdminRoutes } from "./WorkspaceAdminRoutes";
import { WorkspaceAdminTabs } from "./WorkspaceAdminTabs";

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: auto auto 1fr;
  padding: 20px 30px 5px 30px;
  gap: 16px;
  line-height: 1.3;
`;

const Header = styled.div`
  display: flex;

  button.importBtn {
    margin-left: auto;
  }
  h1 {
    font-weight: 800;
    font-size: 28px;
    line-height: 56px;
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;

  h1 {
    margin: 0px;
  }

  .coeff__select {
    margin-left: 24px;

    .ant-select-selector {
      width: 148px;
      height: 32px;
      background-color: #f5f5f5;
      .ant-select-selection-item {
        font-weight: 600;
      }
    }
  }
`;

export enum TabName {
  imports = "imports",
  runs = "runs",
  users = "users",
  alerts = "alerts",
  web_dashboards = "web_dashboards",
  data_sources = "data_sources",
  writebacks = "writebacks",
  writeback_runs = "writeback_runs",
  settings = "settings",
}

type WorkspaceAdminProps = {
  baseUrl?: string;
  hideHeader?: boolean;
  coeffAdminDomain?: string;
};

export const WorkspaceAdmin: React.FC<WorkspaceAdminProps> = ({
  baseUrl = "/dashboard/admin",
  hideHeader,
  coeffAdminDomain,
}) => {
  const track = useTrack();

  const history = useHistory();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const { domain: userDomain } = useSelector((state: AppState) => ({
    domain: state.app.domain,
  }));

  const { isGsuiteDomainAdmin, domains_in_domain_family } = useSelector((state: AppState) => ({
    isGsuiteDomainAdmin: state.app.isGsuiteDomainAdmin,
    domains_in_domain_family: state.app.domains_in_domain_family,
  }));

  const getFallbackDomain = () => {
    return coeffAdminDomain ?? domains_in_domain_family.length > 1 ? "all_domains" : userDomain;
  };

  const [domain, setDomain] = React.useState(searchParams.get("domain") ?? getFallbackDomain());

  const setDomainInSearchParams = (domain: string) => {
    const searchParams = new URLSearchParams(location.search);

    searchParams.set("domain", domain);

    history.push({ pathname: location.pathname, search: searchParams.toString() });
  };

  useEffect(() => {
    document.title = "Admin - Coefficient Workspace";

    track("workspace_admin_data_sources_list_viewed");
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (domain && searchParams.get("domain") !== domain) {
      setDomainInSearchParams(domain);
    }
  }, [domain]);

  if (!isGsuiteDomainAdmin) {
    return null;
  }

  return (
    <Container>
      <Header style={{ alignItems: "center" }}>
        {!hideHeader && (
          <Title>
            <h1>Admin</h1>

            {domains_in_domain_family.length > 1 && (
              <Select
                onChange={domain => setDomain(domain)}
                size="middle"
                value={domain}
                placeholder="Choose domain..."
              >
                <Option key="all_domains" value="all_domains">
                  All Domains
                </Option>

                {domains_in_domain_family.map((column, i) => (
                  <Option key={column} value={column}>
                    {column}
                  </Option>
                ))}
              </Select>
            )}
          </Title>
        )}
      </Header>

      <Header>
        <WorkspaceAdminTabs domain={domain} baseUrl={baseUrl} />
      </Header>

      <ErrorBoundary onBack={() => history.push("/dashboard")}>
        <WorkspaceAdminRoutes baseUrl={baseUrl} domain={domain} />
      </ErrorBoundary>
    </Container>
  );
};
